import React from "react";
import { Stack, Chip, Box } from "@mui/material";
import { GemelDataFilteredView } from "../types";
import { formatHandlerCode } from "../../../utils/namesFormater";
import { colorHandlerCode } from "../../../utils/colorEnumFormatter";
import { stockExposureColors, foreignExposureColors, currencyExposureColors, getSubspecializationColor } from "../../../utils/fundsFormatter";

interface ActiveFiltersChipsProps {
  filters: GemelDataFilteredView;
  onRemoveFilter: (filterType: 'handler' | 'subSpec' | 'stock' | 'foreign' | 'currency', value: string) => void;
}

// קונפיגורציה של צבעים לכל סוג פילטר
const FILTER_COLORS = {
  handler: {
    border: '#1976d2',      // primary blue
    indicator: '#1976d2',
  },
  subSpec: {
    border: '#9c27b0',      // secondary purple
    indicator: '#9c27b0',
  },
  stock: {
    border: '#0288d1',      // info blue
    indicator: '#0288d1',
  },
  foreign: {
    border: '#2e7d32',      // success green
    indicator: '#2e7d32',
  },
  currency: {
    border: '#ed6c02',      // warning orange
    indicator: '#ed6c02',
  },
};

export const ActiveFiltersChips: React.FC<ActiveFiltersChipsProps> = ({
  filters,
  onRemoveFilter,
}) => {
  const hasActiveFilters =
    filters.handlers.length > 0 ||
    filters.subSpecializations.length > 0 ||
    filters.stockExposureRanges.length > 0 ||
    filters.foreignExposureRanges.length > 0 ||
    filters.currencyExposureRanges.length > 0;

  if (!hasActiveFilters) {
    return null;
  }

  return (
    <Stack direction="row" spacing={1} sx={{ flexWrap: 'wrap', gap: 1 }}>
      {filters.handlers.map((handler) => {
        const handlerColor = colorHandlerCode(handler);
        return (
          <Chip
            key={`handler-${handler}`}
            label={
              <Box display="flex" alignItems="center" gap={0.75}>
                <Box
                  sx={{
                    width: 14,
                    height: 14,
                    borderRadius: "50%",
                    bgcolor: handlerColor,
                  }}
                />
                <span style={{ color: '#fff', fontWeight: 500 }}>יצרן: {formatHandlerCode(handler)}</span>
              </Box>
            }
            size="medium"
            onDelete={() => onRemoveFilter('handler', handler)}
            sx={{
              bgcolor: FILTER_COLORS.handler.border,
              color: '#fff',
              '&:hover': {
                bgcolor: FILTER_COLORS.handler.border,
                opacity: 0.9,
              },
              '& .MuiChip-deleteIcon': {
                color: '#fff',
                '&:hover': {
                  color: '#f5f5f5',
                },
              },
            }}
          />
        );
      })}
      {filters.subSpecializations.map((subSpec) => {
        const subSpecColor = getSubspecializationColor(subSpec);
        return (
          <Chip
            key={`subspec-${subSpec}`}
            label={
              <Box display="flex" alignItems="center" gap={0.75}>
                <Box
                  sx={{
                    width: 14,
                    height: 14,
                    borderRadius: "50%",
                    bgcolor: subSpecColor,
                  }}
                />
                <span style={{ color: '#fff', fontWeight: 500 }}>התמחות: {subSpec}</span>
              </Box>
            }
            size="medium"
            onDelete={() => onRemoveFilter('subSpec', subSpec)}
            sx={{
              bgcolor: FILTER_COLORS.subSpec.border,
              color: '#fff',
              '&:hover': {
                bgcolor: FILTER_COLORS.subSpec.border,
                opacity: 0.9,
              },
              '& .MuiChip-deleteIcon': {
                color: '#fff',
                '&:hover': {
                  color: '#f5f5f5',
                },
              },
            }}
          />
        );
      })}
      {filters.stockExposureRanges.map((range) => {
        const rangeColor = stockExposureColors[range];
        return (
          <Chip
            key={`stock-${range}`}
            label={
              <Box display="flex" alignItems="center" gap={0.75}>
                <Box
                  sx={{
                    width: 14,
                    height: 14,
                    borderRadius: "50%",
                    bgcolor: rangeColor,
                  }}
                />
                <span style={{ color: '#fff', fontWeight: 500 }}>מניות: {range}%</span>
              </Box>
            }
            size="medium"
            onDelete={() => onRemoveFilter('stock', range)}
            sx={{
              bgcolor: FILTER_COLORS.stock.border,
              color: '#fff',
              '&:hover': {
                bgcolor: FILTER_COLORS.stock.border,
                opacity: 0.9,
              },
              '& .MuiChip-deleteIcon': {
                color: '#fff',
                '&:hover': {
                  color: '#f5f5f5',
                },
              },
            }}
          />
        );
      })}
      {filters.foreignExposureRanges.map((range) => {
        const rangeColor = foreignExposureColors[range];
        return (
          <Chip
            key={`foreign-${range}`}
            label={
              <Box display="flex" alignItems="center" gap={0.75}>
                <Box
                  sx={{
                    width: 14,
                    height: 14,
                    borderRadius: "50%",
                    bgcolor: rangeColor,
                  }}
                />
                <span style={{ color: '#fff', fontWeight: 500 }}>חו"ל: {range}%</span>
              </Box>
            }
            size="medium"
            onDelete={() => onRemoveFilter('foreign', range)}
            sx={{
              bgcolor: FILTER_COLORS.foreign.border,
              color: '#fff',
              '&:hover': {
                bgcolor: FILTER_COLORS.foreign.border,
                opacity: 0.9,
              },
              '& .MuiChip-deleteIcon': {
                color: '#fff',
                '&:hover': {
                  color: '#f5f5f5',
                },
              },
            }}
          />
        );
      })}
      {filters.currencyExposureRanges.map((range) => {
        const rangeColor = currencyExposureColors[range];
        return (
          <Chip
            key={`currency-${range}`}
            label={
              <Box display="flex" alignItems="center" gap={0.75}>
                <Box
                  sx={{
                    width: 14,
                    height: 14,
                    borderRadius: "50%",
                    bgcolor: rangeColor,
                  }}
                />
                <span style={{ color: '#fff', fontWeight: 500 }}>מט"ח: {range}%</span>
              </Box>
            }
            size="medium"
            onDelete={() => onRemoveFilter('currency', range)}
            sx={{
              bgcolor: FILTER_COLORS.currency.border,
              color: '#fff',
              '&:hover': {
                bgcolor: FILTER_COLORS.currency.border,
                opacity: 0.9,
              },
              '& .MuiChip-deleteIcon': {
                color: '#fff',
                '&:hover': {
                  color: '#f5f5f5',
                },
              },
            }}
          />
        );
      })}
    </Stack>
  );
};
